import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { env } from "../../constants/env";
import { getCookie } from "../../utilities/functions";
import { useDomainContext } from "../../contexts/DomainContext";
import { useThemeColors } from "../../hooks/useThemeColors";
import { useDynamicTitle } from "../../hooks/useDynamicTitle";
import classes from "./AddMember.module.css";
import bulkClasses from "./BulkRequest.module.css";

const DEFAULT_ACCOUNT_TYPE = "Instant Funding (DFY)";

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const BulkRequest = () => {
  useDynamicTitle("Bulk Request", "dashboard/team");
  const [selectedAccountTypes, setSelectedAccountTypes] = useState([DEFAULT_ACCOUNT_TYPE]);
  const [paymentLink, setPaymentLink] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [rowCount, setRowCount] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const { companyTheme } = useDomainContext();
  const { PRIMARY_COLOR } = useThemeColors();
  const companyId = getCookie("companyId");

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadedFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const lines = text.split("\n").filter((l) => l.trim().length > 0);
      setRowCount(Math.max(0, lines.length - 1));
    };
    reader.readAsText(file);
  };

  const submitHandler = async () => {
    if (!uploadedFile || selectedAccountTypes.length === 0) return;

    const formData = new FormData();
    formData.append("file", uploadedFile);
    formData.append("accountTypes", JSON.stringify(selectedAccountTypes));
    formData.append("paymentLink", paymentLink);
    formData.append("company", companyId);

    try {
      setIsSending(true);
      const response = await fetch(`${env.primeBridgeUrl}/api/company/bulk-request`, {
        method: "POST",
        headers: { linkname: companyTheme?.name },
        body: formData,
      });

      if (!response.ok) {
        const result = await response.json();
        toast.error(result.error || "Bulk request failed.");
        return;
      }

      const result = await response.json();
      toast.success(`Submitted ${result.created} new user${result.created !== 1 ? "s" : ""}${result.skipped > 0 ? `, ${result.skipped} skipped` : ""}.`);
      setSelectedAccountTypes([DEFAULT_ACCOUNT_TYPE]);
      setPaymentLink("");
      setUploadedFile(null);
      setRowCount(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
      console.log(error);
    } finally {
      setIsSending(false);
    }
  };

  const canSubmit = selectedAccountTypes.length > 0 && uploadedFile;

  return (
    <div className={`${classes.wrapper} container`}>
      <div
        className={`${classes.breadCrumbs} d-flex justify-content-between align-items-baseline`}
      >
        <div>
          <h6 onClick={() => navigate("/dashboard/team")} role="button">
            Team
          </h6>
          <h3>Bulk Request</h3>
        </div>
      </div>

      <h5>Account Details</h5>

      <div className={classes.formWrapper}>
        <input
          value={DEFAULT_ACCOUNT_TYPE}
          readOnly
        />
        <input
          placeholder="Payment Link"
          value={paymentLink}
          onChange={(e) => setPaymentLink(e.target.value)}
        />
      </div>

      <h5>Upload File</h5>

      <input
        type="file"
        accept=".csv"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      />

      {uploadedFile ? (
        <div className={bulkClasses.fileSuccess}>
          <div className="d-flex align-items-center gap-2">
            <span className={bulkClasses.successIcon}>&#10003;</span>
            <span className={bulkClasses.successLabel}>Success</span>
          </div>
          <div>
            <div className={bulkClasses.fileName}>{uploadedFile.name}</div>
            <div className={bulkClasses.fileMeta}>
              {formatFileSize(uploadedFile.size)}
              {rowCount !== null && `, ${rowCount.toLocaleString()} rows`}
            </div>
          </div>
          <button
            className={bulkClasses.reuploadBtn}
            style={{ color: PRIMARY_COLOR }}
            onClick={() => fileInputRef.current.click()}
          >
            Change
          </button>
        </div>
      ) : (
        <button
          className={bulkClasses.uploadBtn}
          onClick={() => fileInputRef.current.click()}
        >
          &#8679; Upload file
        </button>
      )}

      <div className="d-flex justify-content-center align-items-center">
        {isSending ? (
          <div className="spinner-border" role="status" />
        ) : (
          <button
            className={`addbutton ${!canSubmit ? classes.disabledButton : ""}`}
            style={{ background: canSubmit ? PRIMARY_COLOR : undefined }}
            disabled={!canSubmit}
            onClick={submitHandler}
          >
            Submit
          </button>
        )}
      </div>
    </div>
  );
};     

export default BulkRequest;
